/* eslint-disable @typescript-eslint/no-unused-vars */
import { MenuItemType } from 'antd/es/menu/hooks/useItems';
import dayjs from 'dayjs';
import { StatusiKonkurrent } from '../../../constants/enums/statusi-konkurimit';
import { SidebarMenuOptionsProps } from '../../../constants/SidebarMenuOptions';
import { Statusi } from '../../../utilities/enums';
import { SearchCategory } from '../../../utilities/global-search-enums';
import { ROLES } from '../../../constants/enums/roles';
import { TraineeByIdProps } from '../../../interfaces/training';
import {
  KonkurrentiData,
  PerdoruesVerifikues,
} from '../../../interfaces/konkurrenti-interface';
import { UserData } from '../../../interfaces/Auth';
import { documentCategories } from '../../../pages/Konkurrim/documents/document-list';
import { AcademicYear } from '../../../interfaces/CompetitionYear';
import { PaginationProps } from '../../../interfaces/pagination';
import { NavigateFunction } from 'react-router';
import { SearchResultItem } from '../../../interfaces/global-search';
import { allAnetarKomisioni } from '../../../constants/all-anetar-komisioni';

type RoleType = ROLES | undefined;

export const capitalizeText = (text = '', uppercase = false) => {
  if (!text) return '';

  const modifiedText = text.replace(/_/g, ' ');
  const slicedText = modifiedText.slice(1);
  return (
    modifiedText.charAt(0).toUpperCase() +
    (uppercase ? slicedText.toUpperCase() : slicedText.toLowerCase())
  );
};

export const initialPage = (): PaginationProps => ({
  page: 0,
  size: 10,
});
export const addFilter = (
  queryString: string,
  filter: string,
  hasFilter: boolean,
) => {
  if (!hasFilter) {
    queryString += `?${filter}`;
    hasFilter = true;
  } else {
    queryString += `&${filter}`;
  }
  return { queryString, hasFilter };
};

export const statusArray = [
  StatusiKonkurrent.new,
  StatusiKonkurrent.aplikant,
  StatusiKonkurrent.neVerifikim,
  StatusiKonkurrent.refuzuar,
];

// Callers pass date-fns-style tokens; bridge the ones in use to dayjs tokens.
const toDayjsPattern = (pattern: string) =>
  pattern.replace(/yyyy/g, 'YYYY').replace(/yy/g, 'YY').replace(/dd/g, 'DD');

export const convertDate = (
  value?: Date | string,
  formatType = 'yyyy-MM-dd',
  toLocalDateTime = false,
) => {
  if (!value) {
    return '';
  }

  const date = dayjs(new Date(value));
  const pattern = toDayjsPattern(formatType);

  return toLocalDateTime
    ? date.format(`${pattern}, HH:mm`)
    : date.format(pattern);
};

export const getAllValidPaths = (items: SidebarMenuOptionsProps[]) => {
  const paths: string[] = [];

  for (let index = 0; index < items.length; index++) {
    const item = items[index];
    paths.push(item.key);
    if (item.children && Array.isArray(item.children)) {
      item.children.map((child: MenuItemType) => {
        if (child && typeof child === 'object' && 'key' in child) {
          paths.push(child.key as string);
        }
      });
    }
  }

  return paths;
};

export const { krijuar, miratuar, perfunduar } = Statusi;

export const {
  DOKUMENT,
  PJESEMARRES,
  TRAJNIMI,
  EKSPERT_TRAJNIMI,
  TRAJNIM_PREZENCA,
  KONKURRENT,
  KANDIDAT,
  LENDA,
  LISTE_PREZENCA,
  PEDAGOG,
  EVIDENCA,
} = SearchCategory;

export const getActiveUser = () => {
  return (localStorage.getItem('activeUser') ??
    localStorage.getItem('moduleUser')) as RoleType;
};

export const getFirstCharacters = (value = '') => {
  const words = value.split(' ');
  const initials = words
    .filter(item => isNaN(Number(item.charAt(0))))
    .map(item => item.charAt(0).toUpperCase());
  return initials.slice(0, 2).join('');
};

export const {
  admin,
  konkurrent,
  anetarShm,
  specialistFv,
  pjesmarres,
  pedagog,
  specialistFF,
  student,
  pergjegjesDepartamentiFv,
  kancelar,
  anetarKlp,
  anetarAsh,
  anetarKlgj,
} = ROLES;

export const removeEmptyParams = <T extends object>(obj: T) => {
  const cleaned = Object.fromEntries(
    Object.entries(obj).filter(
      ([_, value]) => value !== '' && value !== null && value !== undefined,
    ),
  ) as Partial<T>;

  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
};

export const getVitiAkademik = (data: TraineeByIdProps) => {
  if (data?.vitiAkademik) {
    return getFullAcademicYear(data.vitiAkademik);
  }
  if (data?.programTematikKalendar?.vitiFillimit) {
    const { vitiFillimit, vitiMbarimit = '' } = data.programTematikKalendar;

    return `${vitiFillimit} - ${vitiMbarimit}`;
  }
  if (data?.programTematikKalendar?.vitiAkademik) {
    const { vitiFillimit = '', vitiMbarimit = '' } =
      data.programTematikKalendar.vitiAkademik;

    return `${vitiFillimit} - ${vitiMbarimit}`;
  }
  return '-';
};

export const getFullAcademicYear = (data?: AcademicYear) => {
  if (data) {
    return `${data.vitiFillimit} - ${data.vitiMbarimit}`;
  }

  return '-';
};

export const getFullName = (konkurenti?: KonkurrentiData | UserData) => {
  if (konkurenti) {
    return konkurenti.personi?.emri + ' ' + konkurenti.personi?.mbiemri;
  }
  return '';
};

export const shouldShowTopBar = () => {
  const now = new Date();
  const month = now.getMonth();
  const day = now.getDate();
  const year = now.getFullYear();
  const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  const lastDayOfFeb = isLeapYear ? 29 : 28;

  return month === 1 && day >= 6 && day <= lastDayOfFeb;
};

export const getCurrentYear = () => new Date().getFullYear();

export const getTotalDocumentsRequired = () => {
  return documentCategories.reduce(
    (total, category) => total + category.documents.length,
    0,
  );
};

export const navigateWithSearchState = (
  navigate: NavigateFunction,
  url: string,
  result: SearchResultItem,
) => {
  navigate(url, {
    state: {
      searchValue: result?.title,
      type: result?.category,
      result,
    },
  });
  return;
};

export const shmRole = (perdoruesVerifikues: PerdoruesVerifikues) => {
  const role = perdoruesVerifikues.rolet.find(item =>
    allAnetarKomisioni.includes(item),
  );
  return role;
};
